"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Compass, FlaskConical, Bug, Users, Presentation, Route, Globe as Globe2, CircleCheck as CheckCircle2, ShieldCheck, Cpu } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
type Lang = "vi" | "en";
interface ExpectationItem {
  id: string;
  icon: React.ElementType;
  title: {
    vi: string;
    en: string;
  };
  desc: {
    vi: string;
    en: string;
  };
  step: string;
}
const expectations: ExpectationItem[] = [{
  id: "exp-1",
  icon: Compass,
  step: "01",
  title: {
    vi: "Con được hướng dẫn trong quá trình thực hành",
    en: "Your child is guided through hands-on practice"
  },
  desc: {
    vi: "Giảng viên đồng hành, gợi mở tư duy thay vì làm hộ, giúp con làm chủ kĩ năng thực tế.",
    en: "Instructors facilitate thinking and exploration rather than doing the work for the child."
  }
}, {
  id: "exp-2",
  icon: FlaskConical,
  step: "02",
  title: {
    vi: "Con có cơ hội tự thử nghiệm giải pháp",
    en: "Your child has the chance to test their own solutions"
  },
  desc: {
    vi: "Khuyến khích con đưa ra giả thuyết, lắp ráp và lập trình theo ý tưởng riêng.",
    en: "Children are encouraged to test hypotheses, assemble, and program their own creative ideas."
  }
}, {
  id: "exp-3",
  icon: Bug,
  step: "03",
  title: {
    vi: "Con học cách kiểm tra và sửa lỗi",
    en: "Your child learns to check and fix errors"
  },
  desc: {
    vi: "Lỗi sai là cơ hội học tập. Con rèn luyện tính kiên nhẫn khi tự tìm và sửa lỗi thuật toán.",
    en: "Errors are learning opportunities. Children build resilience by debugging hardware and code."
  }
}, {
  id: "exp-4",
  icon: Users,
  step: "04",
  title: {
    vi: "Con được làm việc với bạn",
    en: "Your child works alongside peers"
  },
  desc: {
    vi: "Học tập theo nhóm giúp con nâng cao kĩ năng giao tiếp, lắng nghe và phân công công việc.",
    en: "Group projects foster communication, active listening, and constructive collaboration."
  }
}, {
  id: "exp-5",
  icon: Presentation,
  step: "05",
  title: {
    vi: "Con có cơ hội trình bày sản phẩm khi phù hợp",
    en: "Your child has the opportunity to present their work when appropriate"
  },
  desc: {
    vi: "Luyện tập diễn đạt tư duy thiết kế và nguyên lý hoạt động của mô hình trước lớp.",
    en: "Practice articulating design logic and mechanism functions confidently in front of peers."
  }
}, {
  id: "exp-6",
  icon: Route,
  step: "06",
  title: {
    vi: "Phụ huynh có thể tìm hiểu lộ trình học phù hợp với độ tuổi và mục tiêu của con",
    en: "Parents can explore a learning path suited to their child's age and goals"
  },
  desc: {
    vi: "Đội ngũ chuyên môn tư vấn chi tiết các mốc phát triển chuẩn STEM dành riêng cho từng độ tuổi.",
    en: "Expert guidance on progressive STEM milestones tailored to your child's age group."
  }
}];
export default function ParentExpectations() {
  const [lang, setLang] = useState<Lang>("vi");
  return <section id="parentexpectations" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Top Precision Spec Rail Accent Line with Technical Notch */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-border/60">
        <div className="max-w-7xl mx-auto h-full relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-1 bg-primary" />
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-secondary rounded-b-sm" />
        </div>
      </div>

      {/* Background Decorative Tech Grid Gridlines */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.05] bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:3rem_3rem]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header & Language Toggle Container */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 lg:mb-16 space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-3 py-1 border-primary/30 text-primary bg-primary/5">
              <CheckCircle2 className="size-3.5 text-primary mr-1" />
              {lang === "vi" ? "Đồng hành thực chất" : "Realistic Guidance"}
            </Badge>

            {/* Language Switcher */}
            <div className="inline-flex items-center bg-muted rounded-full p-1 border border-border">
              <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => setLang("vi")} className="h-7 text-xs rounded-full px-3">
                <Globe2 className="size-3 mr-1" />
                VIE
              </Button>
              <Button variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => setLang("en")} className="h-7 text-xs rounded-full px-3">
                <Globe2 className="size-3 mr-1" />
                ENG
              </Button>
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-serif text-foreground tracking-tight">
            {lang === "vi" ? "PHỤ HUYNH CÓ THỂ KỲ VỌNG ĐIỀU GÌ?" : "What Can Parents Expect?"}
          </h2>

          <div className="w-16 h-1 bg-secondary rounded-full my-2" />

          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl">
            {lang === "vi" ? "ROBOTO tập trung vào trải nghiệm học tập thực tế, giúp con phát triển năng lực tư duy kĩ thuật bền vững." : "ROBOTO focuses on authentic learning experiences, nurturing sustainable technical thinking skills."}
          </p>
        </div>

        {/* Supporting Photograph Banner in ROBOTO Deep Technical Branding */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="mb-12 lg:mb-16 relative rounded-2xl border border-border/80 bg-card overflow-hidden shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center">
            {/* Visual Photography Box */}
            <div className="lg:col-span-7 relative h-64 sm:h-80 lg:h-96 overflow-hidden bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_parent_expectations_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_parent_expectations_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_parent_expectations_lab.png-wvc-sizes" alt={lang === "vi" ? "Lớp học Robotics thực hành thực tế tại ROBOTO" : "Hands-on robotics learning environment at ROBOTO"} loading="lazy" className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105" />
              {/* Technical Gradient Scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-foreground/30 lg:to-foreground/90" />
              
              {/* Tech Spec Badge Overlay */}
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-foreground/85 backdrop-blur-md text-background px-3 py-1.5 rounded-lg border border-white/20 text-xs font-mono">
                <Cpu className="size-3.5 text-secondary" />
                <span>STEM LAB ACADEMY // ROBOTO</span>
              </div>
            </div>

            {/* Deep Navy/Violet Tech Context Panel */}
            <div className="lg:col-span-5 p-6 sm:p-8 lg:p-10 bg-foreground text-background relative flex flex-col justify-center h-full">
              {/* Precision Spec Rail Accent */}
              <div className="w-12 h-1 bg-secondary mb-4 rounded-full" />
              
              <div className="flex items-center gap-2 mb-2 text-secondary text-xs font-semibold uppercase tracking-wider">
                <ShieldCheck className="size-4" />
                <span>{lang === "vi" ? "TIÊU CHUẨN ĐÀO TẠO" : "ACADEMY STANDARDS"}</span>
              </div>

              <p className="text-background/90 text-sm sm:text-base leading-relaxed font-sans font-medium">
                {lang === "vi" ? "Môi trường học tập tiêu chuẩn công nghệ cao giúp phụ huynh hoàn toàn yên tâm về chất lượng trải nghiệm và sự phát triển tư duy tự chủ của con." : "Our high-tech academy environment gives parents full peace of mind regarding real skill building and independent problem-solving growth."}
              </p>

              {/* Technical Spec Matrix Chips */}
              <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="text-secondary font-mono font-bold">1:6</div>
                  <div className="text-background/70">{lang === "vi" ? "Tỉ lệ Giảng viên/Học viên" : "Instructor Ratio"}</div>
                </div>
                <div>
                  <div className="text-secondary font-mono font-bold">100%</div>
                  <div className="text-background/70">{lang === "vi" ? "Dụng cụ STEM tiêu chuẩn" : "Hands-on Hardware"}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Modular Grid of Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {expectations.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: i * 0.06
          }}>
                <Card className="h-full border border-border/80 bg-card hover:border-secondary/60 hover:shadow-md transition-all duration-300 group relative overflow-hidden">
                  {/* Subtle Corner Notch Indicator */}
                  <div className="absolute top-0 right-0 w-8 h-8 bg-muted/50 rounded-bl-xl border-b border-l border-border/40 flex items-center justify-center text-[10px] font-mono text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                    {item.step}
                  </div>

                  <CardHeader className="pt-6 pb-2">
                    <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <IconComponent className="size-6 shrink-0" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground leading-snug group-hover:text-primary transition-colors">
                      {item.title[lang]}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="pb-6">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc[lang]}
                    </p>
                  </CardContent>

                  {/* Hover Bottom Spec Rail */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-transparent group-hover:bg-secondary transition-colors" />
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}